import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { hasNotifications } from '@guabi/shared';
import { useMarkSeen, useNotifications } from './notifications';

/**
 * What the other person did, and what is about to bite.
 *
 * **Opening it marks the activity read, but does not clear the list.** Those are
 * different things: the badge is "since you last looked", the list is "what has
 * happened". A bell that empties when opened makes you afraid to open it.
 *
 * A deadline is never marked read, because it has not been dealt with — it stops
 * showing when the task is done or the payment is paid, which is the only honest
 * way for it to go away.
 */
export function NotificationBell({ wedId }: { wedId: string }) {
  const { t } = useTranslation();
  const notifications = useNotifications(wedId);
  const markSeen = useMarkSeen(wedId);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const data = notifications.data;
  const dot = data ? hasNotifications(data) : false;
  const unread = data?.unread ?? 0;

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) markSeen.mutate();
  };

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-label={unread > 0 ? t('bell.withUnread', { count: unread }) : t('bell.label')}
        className="relative inline-flex min-h-tap items-center rounded px-2 text-leaf-soft hover:text-ink"
      >
        <Bell />
        {dot && (
          <span
            aria-hidden="true"
            className="absolute right-1 top-2 size-2 rounded-pill bg-brass ring-2 ring-paper"
          />
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label={t('bell.label')}
          className="panel absolute right-0 z-50 mt-2 flex max-h-[28rem] w-[min(22rem,calc(100vw-2rem))] flex-col gap-3 overflow-y-auto p-4"
        >
          {data && data.due.length > 0 && (
            <section className="flex flex-col gap-2">
              <h3 className="eyebrow">{t('bell.due')}</h3>
              <ul className="flex flex-col divide-y divide-line">
                {data.due.map((d) => (
                  <li
                    key={`${d.kind}-${d.title}-${d.dueDate}`}
                    className="flex items-baseline justify-between gap-3 py-2"
                  >
                    <span className="min-w-0 flex-1 text-caption">
                      {d.title}
                      {d.context && <span className="text-leaf-soft"> &middot; {d.context}</span>}
                    </span>
                    <span
                      className={`tabular shrink-0 text-caption ${
                        d.overdue ? 'text-status-declinedFg' : 'text-leaf-soft'
                      }`}
                    >
                      {d.dueDate}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section className="flex flex-col gap-2">
            <h3 className="eyebrow">{t('bell.activity')}</h3>
            {data && data.items.length > 0 ? (
              <ul className="flex flex-col divide-y divide-line">
                {data.items.slice(0, 15).map((item) => (
                  <li key={`${item.at}-${item.entityId}`} className="flex flex-col py-2">
                    <span className="text-caption">
                      {t(`bell.action.${item.action}`, { defaultValue: item.action })}{' '}
                      {t(`bell.entity.${item.entity}`, { defaultValue: item.entity })}
                      {item.summary ? ` — ${item.summary}` : ''}
                    </span>
                    <span className="tabular text-caption text-leaf-soft">
                      {item.at.slice(0, 10)}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-caption text-leaf-soft">{t('bell.quiet')}</p>
            )}
          </section>

          <Link
            to={`/app/w/${wedId}/timeline`}
            onClick={() => setOpen(false)}
            className="min-h-tap text-caption text-brass-text underline underline-offset-4"
          >
            {t('bell.openChecklist')}
          </Link>
        </div>
      )}
    </div>
  );
}

function Bell() {
  return (
    <svg aria-hidden="true" viewBox="0 0 20 20" className="size-5" fill="none">
      <path
        d="M10 3a4.5 4.5 0 0 0-4.5 4.5c0 3-1 4.2-1.6 4.8a.5.5 0 0 0 .35.86h11.5a.5.5 0 0 0 .35-.86c-.6-.6-1.6-1.8-1.6-4.8A4.5 4.5 0 0 0 10 3Z"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinejoin="round"
      />
      <path
        d="M8.3 15.5a1.8 1.8 0 0 0 3.4 0"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
      />
    </svg>
  );
}
